import { useEffect, useState } from 'react';

import { ListDataQueryFunction } from 'api/types/data';

import { UseTableArgument, UseTableFilterParam, useTable } from './useTable';

/**
 * Hook for querying list data with pagination, sorting, and filtering.
 */
export const useQueryListData = <
  T,
  S extends string = string,
  F extends UseTableFilterParam = {},
  P extends any = any,
  Result = any
>(
  useQuery: ListDataQueryFunction<T, F, S, P, Result>,
  {
    limit,
    params = {} as P,
    ...restTableProps
  }: UseTableArgument<S, F> & { limit: number; params?: P }
) => {
  const { sort, onSortChange, onPaginationChange, page, filter, setFilter } =
    useTable(restTableProps);

  const { listData, loading, ...restResult } = useQuery({
    pagination: { page, limit },
    params,
    sort,
    filter,
  });

  const [rowCount, setRowCount] = useState(listData?.total);

  const list: T[] = listData?.list ?? [];

  // Update row count when new data is fetched
  useEffect(() => {
    if (listData?.total !== undefined) {
      setRowCount(listData.total);
    }
  }, [listData?.total]);

  return {
    sort,
    onSortChange,
    page,
    onPaginationChange,
    setFilter,
    filter,
    loading,
    ...restResult,
    rowCount,
    list,
  };
};
